import type { FindNowStage, LocationPoint } from './types';

/**
 * How long a Find Now is allowed to take, and who is allowed to say it failed.
 *
 * The server owns the verdict. It creates the command, drives the stages, and
 * expires the request on its own budget (FIND_NOW_TIMEOUT_MS in
 * apps/api/src/server.ts, 45s by default). The client only ever *reports* what
 * the server already decided.
 *
 * The client used to disagree. It armed its own 30s timer and ran 14 polls,
 * so a phone woken by push — which on a cold Android JS start answers in
 * 33-37s, measured — was declared TIMEOUT here while the server was still
 * legitimately waiting for it, and the next poll then overwrote that terminal
 * state back to CONTACTING_DEVICE. The request read as permanently stuck even
 * though the server recorded SUCCESS.
 *
 * So the client waits longer than the server can, and its own timeout is only
 * a backstop for the case where the server cannot be reached at all.
 */

/**
 * Mirrors the two R2 budgets in apps/api/src/server.ts. Duplicated rather than
 * shared: the two apps have no common package, and findNow.test.ts fails if
 * either drifts from the server's default.
 *
 * R2 split one timeout into two, because they answer different questions. The
 * delivery budget is how long the server waits for any evidence that the phone
 * received the request at all. The command budget is the whole request, and it
 * is deliberately much longer: a phone in Doze is cold-started by the push
 * before it can even begin, and acquiring a real fix after that is not fast.
 */
export const FIND_NOW_SERVER_DELIVERY_BUDGET_MS = 60_000;
export const FIND_NOW_SERVER_BUDGET_MS = 180_000;

/** Gap between command polls. Unchanged; only the number of them grew. */
export const FIND_NOW_POLL_INTERVAL_MS = 2_500;

/**
 * The client's patience: the server's whole budget plus enough margin for the
 * expiry to be written and for one more poll to observe it. Must stay strictly
 * greater than FIND_NOW_SERVER_BUDGET_MS or the client starts declaring
 * failure over a request that is still alive.
 */
export const FIND_NOW_CLIENT_PATIENCE_MS = 190_000;

/**
 * When the waiting copy escalates. Presentation only.
 *
 * A phone that has been idle overnight is cold-started by the push before it
 * can answer, and that takes measurably longer than waking a warm one: ~9-18s
 * warm against ~30-45s cold, observed. Both are the system working. Showing one
 * unchanging line for the whole of that makes a working request look hung, so
 * the wording acknowledges the wait as it lengthens.
 *
 * These describe the wait; they never describe the request. The stage, the
 * command and the 55s patience window are untouched by them, and nothing here
 * may say the device is offline or unreachable while the server still has a
 * live request in flight — it is neither.
 */
export const FIND_NOW_STILL_CONTACTING_MS = 15_000;
export const FIND_NOW_WAKING_MS = 30_000;
export const FIND_NOW_DEEP_IDLE_MS = 75_000;

/** Stages after which the request is over and nothing weaker may replace it. */
export const TERMINAL_FIND_NOW_STAGES: readonly FindNowStage[] = [
  'SUCCESS', 'DELIVERY_TIMEOUT', 'LOCATION_PERMISSION_REQUIRED', 'LOCATION_SERVICES_DISABLED',
  'LOCATION_UNAVAILABLE', 'LOCATION_TIMEOUT', 'NETWORK_UPLOAD_FAILED', 'COMMAND_EXPIRED',
  'DEVICE_OFFLINE', 'PERMISSION_UNAVAILABLE', 'TIMEOUT'
];

export function isTerminalFindNowStage(stage?: FindNowStage | null): boolean {
  return Boolean(stage) && TERMINAL_FIND_NOW_STAGES.includes(stage as FindNowStage);
}

export type FindNowState = {
  requestId: string;
  deviceId: string;
  stage: FindNowStage;
  point?: LocationPoint | null;
  error?: string;
} | null;

export type FindNowUpdate = {
  requestId: string;
  /** Omitted for point-only enrichment, which never changes the stage. */
  stage?: FindNowStage | null;
  point?: LocationPoint | null;
  error?: string | null;
};

/**
 * The single place a Find Now state transition is allowed to happen.
 *
 * Three rules, in order:
 *   - an update for a different request is ignored, so a stale poll or a
 *     socket event for a previous Find Now cannot touch the current one;
 *   - once terminal, a *non-terminal* stage can never be applied again — this
 *     is the invariant that was broken, and it is why every setFindState in
 *     App.tsx routes through here rather than merging inline;
 *   - a terminal stage may still replace a terminal stage, because that is how
 *     a genuine late answer (find-now:late -> SUCCESS) reaches someone who has
 *     already been shown a timeout.
 */
export function applyFindNowUpdate(current: FindNowState, update: FindNowUpdate): FindNowState {
  if (!current || current.requestId !== update.requestId) return current;
  const incoming = update.stage || null;
  if (incoming && isTerminalFindNowStage(current.stage) && !isTerminalFindNowStage(incoming)) return current;
  return {
    ...current,
    stage: incoming || current.stage,
    point: update.point ?? current.point,
    error: update.error ?? current.error
  };
}

export type FindNowCommandSnapshot = {
  findNowStage?: FindNowStage | null;
  status?: string | null;
  point?: LocationPoint | null;
  failureReason?: string | null;
} | null;

export type PollFindNowDependencies = {
  fetchCommand: (requestId: string) => Promise<FindNowCommandSnapshot>;
  onUpdate: (update: FindNowUpdate) => void;
  now?: () => number;
  sleep?: (milliseconds: number) => Promise<void>;
  patienceMs?: number;
  intervalMs?: number;
};

/**
 * Poll one Find Now until the server gives a terminal answer, or until the
 * client's patience runs out.
 *
 * The client timeout lives here rather than in a parallel setTimeout on
 * purpose: two independent writers of the same state is what produced the
 * TIMEOUT-then-CONTACTING_DEVICE flicker. Elapsed time is checked *after* the
 * response is applied, so an answer that lands inside the server's budget
 * always wins over the clock.
 */
export async function pollFindNow(requestId: string, dependencies: PollFindNowDependencies): Promise<void> {
  const now = dependencies.now || Date.now;
  const sleep = dependencies.sleep || ((ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)));
  const patienceMs = dependencies.patienceMs ?? FIND_NOW_CLIENT_PATIENCE_MS;
  const intervalMs = dependencies.intervalMs ?? FIND_NOW_POLL_INTERVAL_MS;
  const startedAt = now();

  for (;;) {
    await sleep(intervalMs);
    const command = await dependencies.fetchCommand(requestId).catch(() => null);
    if (command) {
      if (command.findNowStage) {
        dependencies.onUpdate({
          requestId,
          stage: command.findNowStage,
          point: command.point ?? undefined,
          error: command.failureReason ?? undefined
        });
        if (isTerminalFindNowStage(command.findNowStage)) return;
      }
      // A completed or failed command with no stage cannot become anything
      // else, so there is nothing left to wait for.
      if (command.status === 'COMPLETED' || command.status === 'FAILED') return;
    }
    if (now() - startedAt >= patienceMs) {
      dependencies.onUpdate({
        requestId,
        stage: 'TIMEOUT',
        error: 'The device did not return a fresh location in time.'
      });
      return;
    }
  }
}

export type FindNowWaitingCopy = { title: string; body: string };

/**
 * What to say while waiting, derived from the authoritative stage and how long
 * the client has been waiting. A pure function of those two inputs: it reads
 * state and never writes it, so no wording can move a request forward, hold one
 * back, or restart anything.
 *
 * The stage always wins. Elapsed time only chooses between phrasings of the
 * same stage, and only while CONTACTING_DEVICE — once the server says
 * ACQUIRING_LOCATION the phone has answered and the wait is over, however long
 * it took to get there.
 */
export type FindNowFailureCopy = { label: string; title: string; body: string };

/**
 * The short badge shown beside the failure.
 *
 * Previously this rendered the raw stage enum. R1's vocabulary was small enough
 * to survive that ("TIMEOUT"); R2's is not — "NETWORK UPLOAD FAILED" and
 * "LOCATION SERVICES DISABLED" are internal identifiers, and putting them in
 * front of someone looking for their child is not transparency, it is noise.
 * The stage itself remains available to support through command telemetry.
 */
export function findNowFailureLabel(stage: FindNowStage): string {
  switch (stage) {
    case 'DEVICE_OFFLINE':
    case 'DELIVERY_TIMEOUT': return 'Not delivered';
    case 'PERMISSION_UNAVAILABLE':
    case 'LOCATION_PERMISSION_REQUIRED': return 'Permission needed';
    case 'LOCATION_SERVICES_DISABLED': return 'Location off';
    case 'NETWORK_UPLOAD_FAILED': return 'Could not send';
    case 'COMMAND_EXPIRED': return 'Took too long';
    case 'TIMEOUT':
    case 'LOCATION_TIMEOUT': return 'No fix in time';
    default: return 'No location';
  }
}


/**
 * What a failed request is allowed to say.
 *
 * The one rule R2 adds: only a stage that means the request never reached the
 * phone may say the phone could not be reached. Every other failure describes
 * what happened *on* a phone that demonstrably had the request, because saying
 * "unreachable" about a device that answered is the specific lie this release
 * exists to remove.
 */
export function findNowFailureCopy(stage: FindNowStage, ownerName: string, error?: string | null): FindNowFailureCopy {
  const unreached = stage === 'DEVICE_OFFLINE' || stage === 'DELIVERY_TIMEOUT';
  const title = unreached ? `Unable to reach ${ownerName}’s device`
    : stage === 'PERMISSION_UNAVAILABLE' || stage === 'LOCATION_PERMISSION_REQUIRED' ? 'Location sharing is unavailable'
    : stage === 'LOCATION_SERVICES_DISABLED' ? 'Location is turned off on that phone'
    : 'Unable to get a fresh location';
  const body = error || (
    unreached ? 'The device is not reachable by realtime or push right now. A phone with the app installed can still answer when it regains connectivity.'
    : stage === 'PERMISSION_UNAVAILABLE' || stage === 'LOCATION_PERMISSION_REQUIRED' ? 'The device received your request, but a fresh location is not currently permitted on it.'
    : stage === 'LOCATION_SERVICES_DISABLED' ? 'The device received your request, but location services are switched off on that phone.'
    : stage === 'NETWORK_UPLOAD_FAILED' ? 'The device received your request and found itself, but could not reach LocateMe to send the result back.'
    : stage === 'COMMAND_EXPIRED' ? 'The device received your request but did not finish it in time. If it answers late, the result is still saved and you will be told.'
    : 'The device did not return a fresh location in time. If it answers late, the result is still saved and you will be told.'
  );
  return { label: findNowFailureLabel(stage), title, body };
}

export function findNowWaitingCopy(stage: FindNowStage, elapsedMs: number): FindNowWaitingCopy {
  if (stage === 'CONTACTING_DEVICE' || stage === 'PUSH_SUBMITTED') {
    // R2 waits well past a minute before it will call anything a failure, so
    // the copy has to keep acknowledging the wait rather than freeze and look
    // hung. None of these lines says the device is offline: it is not, and the
    // server has not said so.
    if (elapsedMs >= FIND_NOW_DEEP_IDLE_MS) {
      return { title: 'Waking device…', body: 'A phone that has been idle for a long time takes longer to answer. LocateMe is still waiting.' };
    }
    if (elapsedMs >= FIND_NOW_WAKING_MS) {
      return { title: 'Waking device…', body: 'LocateMe is still waiting for the phone to respond.' };
    }
    if (elapsedMs >= FIND_NOW_STILL_CONTACTING_MS) {
      return {
        title: 'Still contacting device…',
        body: 'The phone may take a little longer to respond when it has been idle for a while.'
      };
    }
    return { title: 'Contacting device…', body: 'Reaching the phone securely.' };
  }
  if (stage === 'ACQUIRING_LOCATION' || stage === 'LOCATION_ACQUIRING' || stage === 'EXECUTION_STARTED') {
    return { title: 'Acquiring location…', body: 'Getting a fresh location from the device.' };
  }
  if (stage === 'DEVICE_REACHED' || stage === 'DEVICE_RECEIVED') {
    return { title: 'Device reached', body: 'The device received your request.' };
  }
  if (stage === 'RESOLVING_ADDRESS') {
    return { title: 'Resolving address', body: 'Converting the confirmed position into a useful place.' };
  }
  if (stage === 'LOCATION_ACQUIRED' || stage === 'RESULT_UPLOADING') return { title: 'Sending location…', body: 'The phone obtained a location and is returning it securely.' };
  return { title: 'Request created', body: 'Your private location request is queued.' };
}
